import * as React from "react";
import { useState } from "react";
import { Field, withTypes } from "react-final-form";
import axios from "axios";

import {
    Button,
    CardActions,
    CircularProgress,
    TextField,
    Card,
    CardContent,
    Typography,
    Box,
    Grid,
    Paper,
    Divider,
    Fade,
    Chip,
    InputAdornment,
} from "@material-ui/core";
import FunctionsIcon from '@material-ui/icons/Functions';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import AccountBalanceIcon from '@material-ui/icons/AccountBalance';
import AttachMoneyIcon from '@material-ui/icons/AttachMoney';
import ScheduleIcon from '@material-ui/icons/Schedule';
import LocalAtmIcon from '@material-ui/icons/LocalAtm';
import { makeStyles } from "@material-ui/core/styles";
import { Notification, useTranslate, useNotify } from "react-admin";
import { ENDPOINTS } from "../apiConstants";
import { getConfigPost } from "../getConfig";

const useStyles = makeStyles((theme) => ({
    main: {
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        alignItems: "center",
        justifyContent: "flex-start",
        padding: theme.spacing(3),
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    },
    container: {
        width: "100%",
        maxWidth: 800,
        marginTop: theme.spacing(4),
    },
    headerCard: {
        marginBottom: theme.spacing(3),
        borderRadius: theme.spacing(2),
        boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
        background: "rgba(255, 255, 255, 0.95)",
        backdropFilter: "blur(10px)",
    },
    formCard: {
        borderRadius: theme.spacing(2),
        boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
        background: "rgba(255, 255, 255, 0.95)",
        backdropFilter: "blur(10px)",
    },
    form: {
        padding: theme.spacing(3),
    },
    input: {
        marginTop: theme.spacing(2),
        "& .MuiOutlinedInput-root": {
            borderRadius: theme.spacing(1),
            "&:hover fieldset": {
                borderColor: theme.palette.primary.main,
            },
        },
    },
    actions: {
        padding: theme.spacing(3),
        display: "flex",
        justifyContent: "center",
    },
    calculateButton: {
        borderRadius: theme.spacing(1),
        textTransform: "none",
        fontWeight: 600,
        padding: theme.spacing(2, 4),
        minHeight: 56,
        fontSize: "1.1rem",
        background: "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
        color: "white",
        boxShadow: "0 4px 16px rgba(33, 150, 243, 0.3)",
        "&:hover": {
            boxShadow: "0 6px 20px rgba(33, 150, 243, 0.4)",
            transform: "translateY(-2px)",
        },
        transition: "all 0.3s ease",
    },
    fieldContainer: {
        marginBottom: theme.spacing(2),
    },
    fieldIcon: {
        marginRight: theme.spacing(1),
        color: theme.palette.primary.main,
    },
    fieldLabel: {
        display: "flex",
        alignItems: "center",
        marginBottom: theme.spacing(1),
        fontWeight: 600,
        color: theme.palette.text.primary,
    },
    headerTitle: {
        fontWeight: 700,
        color: theme.palette.primary.main,
        textAlign: "center",
        marginBottom: theme.spacing(1),
    },
    headerSubtitle: {
        textAlign: "center",
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(2),
    },
    featureChip: {
        margin: theme.spacing(0.5),
        borderRadius: theme.spacing(3),
    },
    featuresContainer: {
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        marginTop: theme.spacing(2),
    },
    loadingContainer: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: theme.spacing(2),
    },
    resultCard: {
        marginTop: theme.spacing(3),
        borderRadius: theme.spacing(2),
        boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
        background: "rgba(255, 255, 255, 0.95)",
        backdropFilter: "blur(10px)",
    },
}));

const renderInput = ({
    meta: { touched, error } = { touched: false, error: undefined },
    input: { ...inputProps },
    ...props
}: any) => {
    const { icon, ...restProps } = props;
    return (
        <TextField
            error={!!(touched && error)}
            helperText={touched && error}
            {...inputProps}
            {...restProps}
            fullWidth
            variant="outlined"
            InputProps={{
                startAdornment: icon ? (
                    <InputAdornment position="start">
                        {icon}
                    </InputAdornment>
                ) : undefined,
            }}
        />
    );
};

interface FormValues {
    mortgage_balance?: string;
    interest_rate?: string;
    mortgage_term?: string;
    reward_percentage?: string;
    spend_amount?: string;
}

const { Form } = withTypes<FormValues>();

const SuperchargedCalculator = () => {
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<any>(null);
    const translate = useTranslate();
    const notify = useNotify();
    const classes = useStyles();

    const handleSubmit = async (data: FormValues) => {
        setLoading(true);
        setResult(null);

        const payload = {
            mortgage_balance: data.mortgage_balance,
            interest_rate: data.interest_rate,
            mortgage_term: data.mortgage_term,
            reward_percentage: data.reward_percentage,
            spend_amount: data.spend_amount,
        };

        try {
            const headers = getConfigPost();
            const response = await axios.post(ENDPOINTS.SUPERCHARGED_CALCULATER, payload, { headers });
            setResult(response.data);
            notify("Supercharged calculation successful", "info");
        } catch (error) {
            notify((error as any)?.response?.data?.message || "Failed to calculate supercharged rate", "error");
        } finally {
            setLoading(false);
        }
    };

    const validate = (values: FormValues) => {
        const errors: FormValues = {};
        if (!values.mortgage_balance) errors.mortgage_balance = translate("ra.validation.required");
        if (!values.interest_rate) errors.interest_rate = translate("ra.validation.required");
        if (!values.mortgage_term) errors.mortgage_term = translate("ra.validation.required");
        if (!values.reward_percentage) errors.reward_percentage = translate("ra.validation.required");
        if (!values.spend_amount) errors.spend_amount = translate("ra.validation.required");
        return errors;
    };

    return (
        <Box className={classes.main}>
            <Box className={classes.container}>
                {/* Header Card */}
                <Card className={classes.headerCard}>
                    <CardContent>
                        <Typography variant="h3" className={classes.headerTitle}>
                            <FunctionsIcon style={{ marginRight: 16, verticalAlign: 'middle', fontSize: '2.5rem' }} />
                            Supercharged Calculator
                        </Typography>
                        <Typography variant="h6" className={classes.headerSubtitle}>
                            Calculate your mortgage savings with our advanced reward system
                        </Typography>
                        <Divider style={{ margin: '16px 0' }} />
                        <div className={classes.featuresContainer}>
                            <Chip 
                                icon={<TrendingUpIcon />} 
                                label="Advanced Analytics" 
                                color="primary" 
                                variant="outlined"
                                className={classes.featureChip}
                            />
                            <Chip 
                                icon={<AttachMoneyIcon />} 
                                label="Real-time Calculations" 
                                color="secondary" 
                                variant="outlined"
                                className={classes.featureChip}
                            />
                            <Chip 
                                icon={<AccountBalanceIcon />} 
                                label="Mortgage Optimization" 
                                color="primary" 
                                variant="outlined"
                                className={classes.featureChip}
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Form Card */}
                <Card className={classes.formCard}>
                    <Form
                        onSubmit={handleSubmit}
                        validate={validate}
                        render={({ handleSubmit }) => (
                            <form onSubmit={handleSubmit} noValidate>
                                <CardContent className={classes.form}>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} md={6}>
                                            <div className={classes.fieldContainer}>
                                                <Typography className={classes.fieldLabel}>
                                                    <AccountBalanceIcon className={classes.fieldIcon} />
                                                    Mortgage Balance
                                                </Typography>
                                                <Field
                                                    name="mortgage_balance"
                                                    component={renderInput}
                                                    label="Enter mortgage balance"
                                                    disabled={loading}
                                                    icon={<AttachMoneyIcon />}
                                                    type="number"
                                                    placeholder="e.g., 250000"
                                                />
                                            </div>
                                        </Grid>
                                        
                                        <Grid item xs={12} md={6}>
                                            <div className={classes.fieldContainer}>
                                                <Typography className={classes.fieldLabel}>
                                                    <LocalAtmIcon className={classes.fieldIcon} />
                                                    Interest Rate
                                                </Typography>
                                                <Field
                                                    name="interest_rate"
                                                    component={renderInput}
                                                    label="Interest rate (%)"
                                                    disabled={loading}
                                                    icon={<LocalAtmIcon />}
                                                    type="number"
                                                    placeholder="e.g., 3.5"
                                                />
                                            </div>
                                        </Grid>
                                        
                                        <Grid item xs={12} md={6}>
                                            <div className={classes.fieldContainer}>
                                                <Typography className={classes.fieldLabel}>
                                                    <ScheduleIcon className={classes.fieldIcon} />
                                                    Mortgage Term
                                                </Typography>
                                                <Field
                                                    name="mortgage_term"
                                                    component={renderInput}
                                                    label="Term in years"
                                                    disabled={loading}
                                                    icon={<ScheduleIcon />}
                                                    type="number"
                                                    placeholder="e.g., 25"
                                                />
                                            </div>
                                        </Grid>
                                        
                                        <Grid item xs={12} md={6}>
                                            <div className={classes.fieldContainer}>
                                                <Typography className={classes.fieldLabel}>
                                                    <TrendingUpIcon className={classes.fieldIcon} />
                                                    Reward Percentage
                                                </Typography>
                                                <Field
                                                    name="reward_percentage"
                                                    component={renderInput}
                                                    label="Reward percentage (%)"
                                                    disabled={loading}
                                                    icon={<LocalAtmIcon />}
                                                    type="number"
                                                    placeholder="e.g., 2.5"
                                                />
                                            </div>
                                        </Grid>
                                        
                                        <Grid item xs={12}>
                                            <div className={classes.fieldContainer}>
                                                <Typography className={classes.fieldLabel}>
                                                    <AttachMoneyIcon className={classes.fieldIcon} />
                                                    Spend Amount
                                                </Typography>
                                                <Field
                                                    name="spend_amount"
                                                    component={renderInput}
                                                    label="spend amount"
                                                    disabled={loading}
                                                    icon={<AttachMoneyIcon />}
                                                    type="number"
                                                    placeholder="e.g., 3000"
                                                />
                                            </div>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                                
                                <CardActions className={classes.actions}>
                                    <Button
                                        variant="contained"
                                        type="submit"
                                        className={classes.calculateButton}
                                        disabled={loading}
                                        startIcon={loading ? <CircularProgress size={24} color="inherit" /> : <FunctionsIcon />}
                                        size="large"
                                    >
                                        {loading ? "Calculating..." : "Calculate Supercharged Rate"}
                                    </Button>
                                </CardActions>
                            </form>
                        )}
                    />
                </Card>

                {/* Result Card */}
                <Fade in={!!result} timeout={500}>
                    <Card className={classes.resultCard}>
                        <CardContent>
                            <Typography variant="h5" className={classes.headerTitle} style={{ marginBottom: 16 }}>
                                <TrendingUpIcon style={{ marginRight: 8, verticalAlign: 'middle' }} />
                                Calculation Results
                            </Typography>
                            <Typography variant="body1" color="textSecondary" align="center" style={{ marginBottom: 24 }}>
                                Your supercharged mortgage calculation is complete!
                            </Typography>
                            
                            {result && result.length > 0 && (
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <Paper elevation={2} style={{ padding: 16, textAlign: 'center', borderRadius: 8 }}>
                                            <Typography variant="h6" color="primary" style={{ fontWeight: 600 }}>
                                                Purchase Amount
                                            </Typography>
                                            <Typography variant="h4" style={{ marginTop: 8, fontWeight: 700 }}>
                                                £{result[0].purchaseAmount}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    
                                    <Grid item xs={12} md={6}>
                                        <Paper elevation={2} style={{ padding: 16, textAlign: 'center', borderRadius: 8 }}>
                                            <Typography variant="h6" color="primary" style={{ fontWeight: 600 }}>
                                                Baseline Reward
                                            </Typography>
                                            <Typography variant="h4" style={{ marginTop: 8, fontWeight: 700 }}>
                                                {result[0].baselineRewardPercent}%
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    
                                    <Grid item xs={12} md={6}>
                                        <Paper elevation={2} style={{ padding: 16, textAlign: 'center', borderRadius: 8 }}>
                                            <Typography variant="h6" color="secondary" style={{ fontWeight: 600 }}>
                                                Reward Amount
                                            </Typography>
                                            <Typography variant="h4" style={{ marginTop: 8, fontWeight: 700 }}>
                                                £{result[0].rewardAmount}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    
                                    <Grid item xs={12} md={6}>
                                        <Paper elevation={2} style={{ padding: 16, textAlign: 'center', borderRadius: 8 }}>
                                            <Typography variant="h6" color="secondary" style={{ fontWeight: 600 }}>
                                                One-off Saving
                                            </Typography>
                                            <Typography variant="h4" style={{ marginTop: 8, fontWeight: 700 }}>
                                                £{result[0].oneOffSavingAmount}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    
                                    <Grid item xs={12} md={6}>
                                        <Paper elevation={2} style={{ padding: 16, textAlign: 'center', borderRadius: 8, background: 'linear-gradient(45deg, #4CAF50 30%, #8BC34A 90%)', color: 'white' }}>
                                            <Typography variant="h6" style={{ fontWeight: 600 }}>
                                                Total Saving Amount
                                            </Typography>
                                            <Typography variant="h4" style={{ marginTop: 8, fontWeight: 700 }}>
                                                £{result[0].totalSavingAmount}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    
                                    <Grid item xs={12} md={6}>
                                        <Paper elevation={2} style={{ padding: 16, textAlign: 'center', borderRadius: 8, background: 'linear-gradient(45deg, #FF9800 30%, #FFC107 90%)', color: 'white' }}>
                                            <Typography variant="h6" style={{ fontWeight: 600 }}>
                                                Supercharged Rate
                                            </Typography>
                                            <Typography variant="h4" style={{ marginTop: 8, fontWeight: 700 }}>
                                                {result[0].superchargedRatePercent}%
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                </Grid>
                            )}
                        </CardContent>
                    </Card>
                </Fade>

                <Notification />
            </Box>
        </Box>
    );
};

export default SuperchargedCalculator;
